import { useCallback } from 'react'

import { useApolloClient, useFragment } from '@apollo/client'
import { Box } from '@factorialco/deprecated-design-system'
import { RichTextDisplay } from '@factorialco/f0-react/dist/experimental'
import {
  MarkProcurementTaskAsDoneDocument,
  ManualTaskContentFragmentDoc,
  ProcurementsApprovalTimelineDocument,
  TaskActivityDocument,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useEmployeeTableViewRow } from 'modules/inbox_tasks/activities/Task/utils/content'
import {
  PossibleTaskState,
  TaskActionsHook,
  TaskCustomComponent,
  TaskDefinition,
} from 'modules/inbox_tasks/lib/abstraction'
import ProcurementPurchaseRequestTaskContent from 'modules/inbox_tasks/tasks/approval/ProcurementPurchaseRequest'

type State = PossibleTaskState

const ProcurementTaskContent: TaskCustomComponent<'ProcurementTask', State> = ({
  taskable,
  taskId,
}) => {
  const { data: taskData } = useFragment({
    fragment: ManualTaskContentFragmentDoc,
    fragmentName: 'ManualTaskContent',
    from: {
      __typename: 'TasksInboxTask',
      id: taskId,
    },
    optimistic: false,
  })

  const purchaseRequest = taskable?.purchaseRequest
  const owner = purchaseRequest?.requesterEmployee?.employeeName ?? undefined

  const requesterContent = useEmployeeTableViewRow({ employee: owner, title: 'Requester' })

  if (!taskable) return null

  const detailsContent = taskData?.content ? (
    <Box flexDirection="column" gap="s4">
      <RichTextDisplay content={taskData.content} className="text-f1-foreground-secondary" />
    </Box>
  ) : null

  return (
    <ProcurementPurchaseRequestTaskContent
      id={purchaseRequest?.purchaseRequestId ?? '0'}
      requesterContent={requesterContent}
      detailsContent={detailsContent}
    />
  )
}

const useProcurementActions: TaskActionsHook<'ProcurementTask', State> = ({ taskable, status }) => {
  const apolloClient = useApolloClient()

  const [markAsDone] = useMutation(MarkProcurementTaskAsDoneDocument, {
    extractorFn: (data) => data.procurementMutations.markAsDoneProcurementTask,
  })

  const handleDropdownAction = useCallback(
    async ({ actionId }: { actionId: string }) => {
      if (actionId !== 'mark-as-done') return

      if (!taskable?.id) return false

      const result = await markAsDone({
        variables: {
          taskId: taskable.id,
        },
      })

      const handled = handleResult(result, () => {})

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('procurement.purchase_requests.detail.responses.general_task.success'),
        })

        apolloClient.refetchQueries({
          include: [TaskActivityDocument, ProcurementsApprovalTimelineDocument],
        })
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('procurement.purchase_requests.detail.responses.general_task.error'),
        })
      }

      return true
    },
    [taskable, markAsDone, apolloClient]
  )

  if (!taskable?.id || status === 'done' || status === 'discarded') {
    return {}
  }

  return {
    onClickDropdownAction: handleDropdownAction,
  }
}

export const ProcurementTaskDefinition: TaskDefinition<'ProcurementTask', State> = {
  taskableTypename: 'ProcurementTask',
  initialState: {},
  component: ProcurementTaskContent,
  useActions: useProcurementActions,
  getTitle: ({ info }) =>
    i18n.t('tasks.inbox.titles.procurement_task', {
      taskName: info.purchaseRequest?.description ?? '',
    }),
  getMetadata: ({ info }) => [info.taskContent || ''],
}
